/**
 * Sticky Add-to-Cart — shows a fixed bar on mobile when the main ATC button
 * scrolls out of viewport. Syncs price & availability on variant change.
 */
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class StickyAtc extends Component {
  #observer = null;
  #resizeObserver = null;
  #variantInput = null;
  #variantObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const mainBtn =
      document.querySelector('product-form [data-add-to-cart]') || document.querySelector('[data-market-notice]');
    if (!mainBtn) return;

    // IntersectionObserver — show sticky bar when main button is out of view
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.classList.add('tw:translate-y-full');
          this.setAttribute('aria-hidden', 'true');
          this.inert = true;
          this.#setReservedHeight(0);
        } else {
          this.classList.remove('tw:translate-y-full');
          this.removeAttribute('aria-hidden');
          this.inert = false;
          this.#setReservedHeight(this.offsetHeight);
        }
      },
      { threshold: 0 }
    );
    this.#observer.observe(mainBtn);

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.hasAttribute('aria-hidden')) this.#setReservedHeight(this.offsetHeight);
      });
      this.#resizeObserver.observe(this);
    }

    // Sticky ATC button click → trigger main form submit
    this.querySelector('[data-sticky-atc-btn]')?.addEventListener('click', this.#onStickyClick);

    // Listen for variant changes to update price & availability
    this.#variantInput = document.querySelector('[data-variant-id-input]');
    if (this.#variantInput) {
      this.#variantInput.addEventListener('change', this.#onVariantChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#setReservedHeight(0);
    this.querySelector('[data-sticky-atc-btn]')?.removeEventListener('click', this.#onStickyClick);
    this.#variantInput?.removeEventListener('change', this.#onVariantChange);
  }

  #setReservedHeight(px) {
    document.documentElement.style.setProperty('--sticky-atc-height', `${px}px`);
  }

  #onStickyClick = async (event) => {
    const btn = event.currentTarget;
    if (btn.disabled) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    try {
      const variantId = document.querySelector('[data-variant-id-input]')?.value;
      if (!variantId) return;

      const cartDrawer = document.querySelector('cart-drawer-component');
      const sectionId = cartDrawer?.dataset.sectionId || 'cart-drawer';

      const items = [{
        id: parseInt(variantId, 10),
        quantity: 1,
      }];

      // Include preferred store property if set
      const storeInput = document.querySelector('[data-preferred-store-input]:not(:disabled)');
      if (storeInput?.value) {
        items[0].properties = {};
        const propName = storeInput.name.replace('properties[', '').replace(']', '');
        items[0].properties[propName] = storeInput.value;
      }

      const body = JSON.stringify({
        items,
        sections: sectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.errors) {
        const msg = data.description || data.errors || 'Could not add to cart';
        document.dispatchEvent(new CartErrorEvent(null, typeof msg === 'string' ? msg : JSON.stringify(msg)));
        btn.classList.remove('is-loading');
        btn.disabled = false;
        return;
      }

      document.dispatchEvent(new CartAddEvent(data, null, {
        sections: data.sections,
        source: 'sticky-atc',
      }));

      // Success animation
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      setTimeout(() => {
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error('Sticky ATC error:', err);
      document.dispatchEvent(new CartErrorEvent(null, err.message));
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  };

  #onVariantChange = () => {
    // Fetch updated section to get new price & availability
    const sectionId = document.querySelector('.product-section')?.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');
    const variantId = this.#variantInput?.value;
    if (!sectionId || !variantId) return;

    fetch(`${window.location.pathname}?variant=${variantId}&section_id=${sectionId}`)
      .then(r => r.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newPrice = doc.querySelector('[data-product-price] [data-price]');
        const newCompare = doc.querySelector('[data-product-price] [data-compare-price]');
        const newAtcText = doc.querySelector('[data-atc-text]');
        const newAtcBtn = doc.querySelector('[data-add-to-cart]');

        // Update price
        const stickyPrice = this.querySelector('[data-sticky-price]');
        const stickyCompare = this.querySelector('[data-sticky-compare-price]');
        if (stickyPrice && newPrice) stickyPrice.textContent = newPrice.textContent;
        if (stickyCompare && newCompare) {
          stickyCompare.textContent = newCompare.textContent;
          stickyCompare.classList.toggle('tw:hidden', newCompare.classList.contains('tw:hidden'));
        }

        // Update button state
        const stickyBtn = this.querySelector('[data-sticky-atc-btn]');
        const stickyText = this.querySelector('[data-sticky-atc-text]');
        if (stickyBtn && newAtcBtn) stickyBtn.disabled = newAtcBtn.disabled;
        if (stickyText && newAtcText) stickyText.textContent = newAtcText.textContent;
      })
      .catch(() => {});
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
